import { apiRequest } from "../../api/apiClient";
import type {
  Officiator,
  CreateOfficiatorDto,
  UpdateOfficiatorDto,
} from "../../types/officiator.types";

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.message ||
        error.title ||
        `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return {} as T;
  }

  return response.json();
};

export const officiatorService = {
  getOfficiatorsByCertificateId: async (
    certificateId: number,
  ): Promise<Officiator[]> => {
    const response = await apiRequest(
      `/Officiators?certificateApplicationId=${certificateId}`,
      {
        method: "GET",
      },
    );
    return handleResponse<Officiator[]>(response);
  },

  getOfficiatorByCertificateApplicationId: async (
    certificateApplicationId: number,
  ): Promise<Officiator | null> => {
    const response = await apiRequest(
      `/Officiators/certificate-application/${certificateApplicationId}`,
      {
        method: "GET",
      },
    );

    if (response.status === 404 || response.status === 204) {
      return null;
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error.message ||
          error.title ||
          `HTTP ${response.status}: ${response.statusText}`,
      );
    }

    const payload = await response.json().catch(() => null);
    if (!payload) return null;

    const resolveOfficiator = (value: any): Officiator | null => {
      if (!value) return null;
      if (Array.isArray(value)) {
        return value.length > 0 ? (value[0] as Officiator) : null;
      }
      if (typeof value === "object") {
        return value as Officiator;
      }
      return null;
    };

    if (payload.data !== undefined) {
      return resolveOfficiator(payload.data);
    }

    return resolveOfficiator(payload);
  },

  getOfficiatorById: async (id: number): Promise<Officiator> => {
    const response = await apiRequest(`/Officiators/${id}`, {
      method: "GET",
    });
    return handleResponse<Officiator>(response);
  },

  createOfficiator: async (data: CreateOfficiatorDto): Promise<Officiator> => {
    const response = await apiRequest("/Officiators", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return handleResponse<Officiator>(response);
  },

  updateOfficiator: async (
    id: number,
    data: UpdateOfficiatorDto,
  ): Promise<Officiator> => {
    const response = await apiRequest(`/Officiators/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return handleResponse<Officiator>(response);
  },

  deleteOfficiator: async (id: number): Promise<void> => {
    const response = await apiRequest(`/Officiators/${id}`, {
      method: "DELETE",
    });
    await handleResponse<void>(response);
  },
};
